/**
 * @file 对局屏（React 外壳）—— 挂载 Pixi 画布 + 会话生命周期 + 悬浮层。
 *
 * 职责边界（ADR-001）：牌桌渲染全部在 Pixi；React 只做
 * 房间等待层 / 结算操作层 / Toast / 错误提示。
 */

import { useEffect, useRef, useState } from 'react';
import { Application } from 'pixi.js';
import { API_ROUTES } from '@majiang/protocol';
import { initTileArt } from '../pixi/tileArt';
import { TableScene } from '../pixi/tableScene';
import type {
  ClientViewState,
  GameMode,
  LocalSettings,
  OnlineConnInfo,
  ReplaySnapshot,
  RoomState,
  SceneAction,
  SessionCallbacks,
  SessionController,
  TableUiState,
} from '../session';
import { LocalSession, OnlineSession } from '../session';
import { postJson } from './http';

export interface GameScreenProps {
  readonly mode: GameMode;
  readonly settings: LocalSettings | null;
  readonly conn: OnlineConnInfo | null;
  readonly onExit: () => void;
  readonly onRestart: () => void;
}

const TOAST_MS = 2600;

export function GameScreen({ mode, settings, conn, onExit, onRestart }: GameScreenProps): React.JSX.Element {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<TableScene | null>(null);
  const appRef = useRef<Application | null>(null);
  const sessionRef = useRef<SessionController | null>(null);
  const roomRef = useRef<RoomState | null>(null);

  const [clientState, setClientState] = useState<ClientViewState | null>(null);
  const [roomState, setRoomState] = useState<RoomState | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [endReason, setEndReason] = useState<string | null>(null);
  const [replay, setReplay] = useState<ReplaySnapshot | null>(null);
  const [, forceRender] = useState(0);

  // —— 多局制状态（单机：rounds 局后总结算；联机恒单局）——
  const totalRounds = mode === 'local' && settings !== null ? settings.rounds : 1;
  const [roundNo, setRoundNo] = useState(1); // 当前局号（1 起）
  const [accum, setAccum] = useState<number[]>([0, 0, 0, 0]); // 四家累计分

  // Toast 自动消失
  useEffect(() => {
    if (toast === null) return;
    const t = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => window.clearTimeout(t);
  }, [toast]);

  // 会话生命周期：创建 Pixi App → TableScene → 会话
  useEffect(() => {
    const host = hostRef.current;
    if (host === null) return;
    let disposed = false;

    const app = new Application();
    appRef.current = app;
    let scene: TableScene | null = null;
    let session: SessionController | null = null;

    const cb: SessionCallbacks = {
      onView: (state, ui: TableUiState) => {
        if (disposed) return;
        setClientState(state);
        if (state.view !== null) scene?.render(state.view, ui);
        forceRender((n) => n + 1); // 让 session.seat 的 getter 反映到 UI
      },
      onRoom: (room) => {
        if (disposed) return;
        roomRef.current = room;
        setRoomState(room);
      },
      onToast: (msg) => {
        if (!disposed) setToast(msg);
      },
      onError: (msg, fatal) => {
        if (!disposed) {
          if (fatal) setFatalError(msg);
          else setToast(msg);
        }
      },
      onEnd: (reason) => {
        if (!disposed) setEndReason(reason);
      },
      onReplay: (snapshot) => {
        if (!disposed) {
          setReplay(snapshot);
          // 多局制：累加四家分数（以本局净分）
          setAccum((prev) => prev.map((p, i) => p + (snapshot.hands[i]?.netScore ?? 0)));
        }
      },
      onDraw: (tileId) => {
        if (!disposed) scene?.animateDraw(tileId);
      },
    };

    const start = async (): Promise<void> => {
      await app.init({
        resizeTo: window,
        background: '#071f17',
        antialias: true,
        resolution: Math.min(window.devicePixelRatio || 1, 2),
      });
      if (disposed) {
        app.destroy(true);
        return;
      }
      await initTileArt(app);
      if (disposed) return;

      scene = new TableScene(app);
      scene.mount(host);
      sceneRef.current = scene;
      scene.onAction((a: SceneAction) => session?.send(a));

      session =
        mode === 'local' && settings !== null
          ? new LocalSession(settings, cb)
          : new OnlineSession(conn!, cb);
      sessionRef.current = session;
      // 多局制：开局传当前局号（mount 时恒为 1）
      await session.start(roundNo);
    };

    void start();

    return () => {
      disposed = true;
      sessionRef.current?.dispose();
      sessionRef.current = null;
      sceneRef.current?.destroy();
      sceneRef.current = null;
      appRef.current?.destroy(true);
      appRef.current = null;
    };
    // 仅挂载时创建一次；重启用 React key 重新挂载
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRoom = async (): Promise<void> => {
    if (conn === null) return;
    try {
      await postJson(API_ROUTES.startRoom(conn.roomCode), { seatToken: conn.seatToken });
    } catch (err) {
      setToast(err instanceof Error ? err.message : String(err));
    }
  };

  const view = clientState?.view ?? null;
  const isEnded = endReason !== null || view?.phase === 'settlement' || view?.phase === 'finished';
  const isOnline = mode === 'online';
  const inLobby = isOnline && view === null;
  const mySeat = sessionRef.current?.seat ?? null;
  const ownerCanStart =
    roomState !== null && !roomState.started && roomState.ownerSeat === mySeat && roomState.members.length === 4;

  return (
    <>
      <div ref={hostRef} className="game-canvas-host" />

      {/* 退出按钮 */}
      {!inLobby && (
        <button
          className="btn ghost"
          style={{ position: 'fixed', top: '0.6rem', right: '0.6rem', zIndex: 15, width: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
          onClick={onExit}
        >
          退出
        </button>
      )}

      {/* 联机等待房 */}
      {inLobby && (
        <div className="overlay">
          <div className="card">
            <h1>等待开局</h1>
            <div className="room-code">{roomState?.roomCode ?? conn?.roomCode ?? '----'}</div>
            <p className="subtitle">把房间码发给好友加入</p>
            <div className="member-list">
              {[0, 1, 2, 3].map((seat) => {
                const m = roomState?.members.find((x) => x.seat === seat);
                return (
                  <div key={seat} className="member">
                    <span>
                      {m === undefined ? '空位' : m.nickname}
                      {m?.isRobot === true ? ' ·AI' : ''}
                    </span>
                    <span className="tag">{m?.connected === true ? '在线' : m === undefined ? '' : '离线'}</span>
                  </div>
                );
              })}
            </div>
            {ownerCanStart && (
              <button className="btn" onClick={() => void startRoom()}>
                满四人，开局
              </button>
            )}
            <button className="btn ghost" onClick={onExit}>
              离开房间
            </button>
            {fatalError !== null && <p className="error">{fatalError}</p>}
          </div>
        </div>
      )}

      {/* 结算 / 结束层 */}
      {isEnded && (
        <div className="overlay">
          <div className="card replay-card">
            <h1>{view?.phase === 'finished' ? '对局结束' : totalRounds > 1 ? `第 ${roundNo} / ${totalRounds} 局结算` : '本局结算'}</h1>
            <p className="subtitle">（牌桌内已展示分数明细）</p>

            {/* 累计分（多局制） */}
            {mode === 'local' && totalRounds > 1 && (
              <div className="score-grid">
                {[0, 1, 2, 3].map((seat) => (
                  <div key={seat} className="score-cell">
                    <span className="score-name">{seat === 0 ? '我' : `AI·${seat}`}</span>
                    <span className={`score-val ${accum[seat]! >= 0 ? 'pos' : 'neg'}`}>
                      {accum[seat]! >= 0 ? '+' : ''}
                      {accum[seat]}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* 复盘：四家手牌（单机模式，本地合法持有） */}
            {mode === 'local' && replay !== null && (
              <div className="replay-box">
                <div className="replay-title">本局手牌复盘</div>
                {replay.hands.map((h) => (
                  <div key={h.seat} className="replay-row">
                    <span className="replay-seat">{h.seat === 0 ? '我' : `AI·${h.seat}`}</span>
                    <span className="replay-hand">
                      {h.handNotation === '' ? '（已胡 / 无手牌）' : h.handNotation}
                      {h.meldNotation.length > 0 ? `　[${h.meldNotation.join(' | ')}]` : ''}
                    </span>
                    <span className={`replay-score ${h.netScore >= 0 ? 'pos' : 'neg'}`}>
                      {h.netScore >= 0 ? '+' : ''}
                      {h.netScore}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="row">
              {mode === 'local' && (
                <>
                  {roundNo < totalRounds ? (
                    <button
                      className="btn"
                      onClick={() => {
                        // 确认 → 下一局（重开会话，保留累计分）
                        sceneRef.current?.resetSettlement();
                        sceneRef.current?.resetWinningTiles();
                        sceneRef.current?.resetClaimed();
                        const next = roundNo + 1;
                        setRoundNo(next);
                        setEndReason(null);
                        setReplay(null);
                        void sessionRef.current?.start(next);
                      }}
                    >
                      确认，下一局
                    </button>
                  ) : (
                    <button className="btn" onClick={onRestart}>
                      {totalRounds > 1 ? '再来一局' : '再来一局'}
                    </button>
                  )}
                </>
              )}
              <button className="btn ghost" onClick={onExit}>
                返回大厅
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 致命错误 */}
      {fatalError !== null && !isEnded && !inLobby && (
        <div className="overlay">
          <div className="card">
            <h1>连接中断</h1>
            <p className="error">{fatalError}</p>
            <button className="btn" onClick={onExit}>
              返回大厅
            </button>
          </div>
        </div>
      )}

      {toast !== null && <div className="toast">{toast}</div>}
    </>
  );
}
